import { message } from "antd";
import logger from "./logger";

// --- helpers -------------------------------------------------
function targetScreenDims() {
  const dpr = window.devicePixelRatio || 1;
  // Cap to 4K; raise if you really want 5K/8K and your pipeline can handle it.
  const maxW = Math.min(Math.round(window.screen.width * dpr), 3840);
  const maxH = Math.min(Math.round(window.screen.height * dpr), 2160);
  return { maxW, maxH };
}

// --- Screen Share --------------------------------------------
export const getScreenStream = async (
  onStop?: () => void
): Promise<MediaStream | null> => {
  const { maxW, maxH } = targetScreenDims();

  try {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        width: { ideal: maxW, max: maxW },
        height: { ideal: maxH, max: maxH },
        frameRate: { ideal: 60, max: 30 },
        cursor: "always",
        // Chromium-only flags (safe to leave in; TS doesn’t know them)
        // @ts-ignore
        surfaceSwitching: "include",
        // @ts-ignore
        preferCurrentTab: false,
      } as MediaTrackConstraints,
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        // @ts-ignore - some browsers support this
        suppressLocalAudioPlayback: false,
      } as MediaTrackConstraints, // share system audio with better settings
    });

    const track = stream.getVideoTracks()[0];
    if (track) {
      if (onStop) track.onended = onStop;

      // For screens/text, this is the big one: prioritize spatial fidelity over motion.
      try {
        (track as any).contentHint = "text";
      } catch {}

      // Try to pin to the maximum the source can do.
      const caps = track.getCapabilities?.() as any;
      const targetW = Math.min(caps?.width?.max ?? maxW, 7680);
      const targetH = Math.min(caps?.height?.max ?? maxH, 4320);
      await track.applyConstraints({
        width: { ideal: targetW },
        height: { ideal: targetH },
        frameRate: { ideal: 60, max: 30 },
        // Not widely typed but helps where supported.
        // @ts-ignore
        resizeMode: "none",
      });

      //.info("[screen] final settings:", track.getSettings?.());
    }

    // Check for audio tracks and provide user feedback
    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) {
      // logger.warn(
      //   "Screen sharing started but no audio tracks captured. User may need to enable 'Share audio' in the browser dialog."
      // );
      message.warning(
        "Screen sharing started but no audio was captured. Please make sure to enable 'Share audio' when prompted by your browser."
      );
    } else {
      // logger.info(
      //   `Screen sharing captured ${audioTracks.length} audio track(s)`
      // );
    }

    return stream;
  } catch (error) {
    console.error(error);
    message.error("Screen share permission denied.");
    return null;
  }
};

// --- Webcam --------------------------------------------------
export const getWebcamStream = async (
  onStop?: () => void
): Promise<MediaStream | null> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: "user",
        width: { ideal: 1920, min: 1280 },
        height: { ideal: 1080, min: 720 },
        frameRate: { ideal: 30, max: 30 },
        // @ts-ignore
        resizeMode: "none",
      },
      audio: false,
    });

    const track = stream.getVideoTracks()[0];
    if (track) {
      if (onStop) track.onended = onStop;
      // For webcam, keep motion smooth.
      try {
        (track as any).contentHint = "motion";
      } catch {}
      //logger.info("[webcam] final settings:", track.getSettings?.());
    }

    return stream;
  } catch (error) {
    console.error(error);
    message.error("Access to webcam was denied.");
    return null;
  }
};

// --- Raw Microphone (no browser DSP, for external processing like RNNoise) ---
export const getRawMicrophoneStream = async (
  onStop?: () => void
): Promise<MediaStream | null> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        sampleRate: 48000,
        channelCount: 1,
        sampleSize: 16,
        latency: 0,
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        // @ts-ignore
        googEchoCancellation: false,
        // @ts-ignore
        googNoiseSuppression: false,
        // @ts-ignore
        googAutoGainControl: false,
        // @ts-ignore
        googHighpassFilter: false,
        // @ts-ignore
        googNoiseReduction: false,
        // @ts-ignore
        mozNoiseSuppression: false,
        // @ts-ignore
        mozAutoGainControl: false,
        // @ts-ignore
        webkitNoiseSuppression: false,
        // @ts-ignore
        webkitAutoGainControl: false,
      } as MediaTrackConstraints,
    });

    const track = stream.getAudioTracks()[0];
    if (track && onStop) track.onended = onStop;

    return stream;
  } catch (error) {
    console.error(error);
    message.error("Access to microphone was denied.");

    try {
      const fallbackStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      return fallbackStream;
    } catch (fallbackError) {
      message.error("Unable to access microphone with any settings.");
      return null;
    }
  }
};

// --- Studio Quality Microphone ------------------------------
export const getMicrophoneStream = async (
  onStop?: () => void
): Promise<MediaStream | null> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        // High quality audio settings
        sampleRate: 48000,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        latency: 0,
        sampleSize: 16,

        // Browser-specific optimizations
        // @ts-ignore - Chrome/Edge specific
        googEchoCancellation: true,
        // @ts-ignore - Chrome/Edge specific
        googNoiseSuppression: true,
        // @ts-ignore - Chrome/Edge specific
        googAutoGainControl: true,
        // @ts-ignore - Chrome/Edge specific
        googHighpassFilter: true,
        // @ts-ignore - Chrome/Edge specific
        googNoiseReduction: true,

        // Firefox specific
        // @ts-ignore - Firefox specific
        mozNoiseSuppression: true,
        // @ts-ignore - Firefox specific
        mozAutoGainControl: true,

        // Safari specific
        // @ts-ignore - Safari specific
        webkitNoiseSuppression: true,
        // @ts-ignore - Safari specific
        webkitAutoGainControl: true,
      } as MediaTrackConstraints,
    });

    const track = stream.getAudioTracks()[0];
    if (track) {
      if (onStop) track.onended = onStop;

      // Log the actual constraints we got
      const settings = track.getSettings();
      // logger.info("[microphone] final settings:", {
      //   sampleRate: settings.sampleRate,
      //   sampleSize: settings.sampleSize,
      //   echoCancellation: settings.echoCancellation,
      //   noiseSuppression: settings.noiseSuppression,
      //   autoGainControl: settings.autoGainControl,
      // });
    }

    return stream;
  } catch (error) {
    console.error(error);
    message.error(
      "Access to microphone was denied or requested quality not available."
    );

    // Fallback to basic audio if high quality fails
    try {
      //logger.warn("Falling back to basic audio constraints");
      const fallbackStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      return fallbackStream;
    } catch (fallbackError) {
      message.error("Unable to access microphone with any settings.");
      return null;
    }
  }
};
